import { StoreObject, TypePolicies } from '@apollo/client';

export function override(existing: any, incoming: any) {
  // a custom function is required to silence the warning: 'Cache data may be lost when replacing...'
  return incoming;
}

function generateKeyWithDefaultVersion(obj: StoreObject) {
  const id = obj.id;
  const language = obj.language;
  const version = obj.version || 'draft';
  return `${obj.__typename}:${id}:${language}:${version}`;
}

function generateColumnKeyWithDefaultVersion(obj: StoreObject) {
  const columnId = obj.columnId;
  const language = obj.language;
  const version = obj.version || 'draft';
  return `${obj.__typename}:${columnId}:${language}:${version}`;
}

export const interactiveLessonTypePolicies: TypePolicies = {
  ActivitiesSection: {
    keyFields: obj => generateKeyWithDefaultVersion(obj),
    fields: {
      components: {
        merge: override,
      },
      subSections: {
        merge: override,
      },
    },
  },
  LessonActivities: {
    keyFields: obj => generateKeyWithDefaultVersion(obj),
    fields: {
      landscapeImage: {
        merge: override,
      },
      sections: {
        merge: override,
      },
      sectionsOverview: {
        merge: override,
      },
      teachingAndLearningSequence: {
        merge: override,
      },
      assetDownloadUrls: {
        merge: override,
      },
      styles: {
        merge(existing, incoming) {
          return {
            ...(existing || {}),
            ...(incoming || {}),
          };
        },
      },
    },
  },
  LessonMaterial: {
    fields: {
      alternativeMaterials: {
        merge: override,
      },
    },
  },
  AssessmentActivities: {
    keyFields: obj => generateKeyWithDefaultVersion(obj),
    fields: {
      sections: {
        merge: override,
      },
      sectionsOverview: {
        merge: override,
      },
      teachingAndLearningSequence: {
        merge: override,
      },
      styles: {
        merge(existing, incoming) {
          return {
            ...(existing || {}),
            ...(incoming || {}),
          };
        },
      },
    },
  },
  ActivitiesActivityComponent: {
    fields: {
      properties: {
        merge(existing, incoming) {
          return {
            ...(existing || {}),
            ...(incoming || {}),
          };
        },
      },
      subActivities: {
        merge: override,
      },
    },
  },
  ActivitiesTeachingLearningSequenceItem: {
    fields: {
      component: {
        merge: override,
      },
      lessonMaterials: {
        merge: override,
      },
      connections: {
        merge: override,
      },
      theEsDetails: {
        merge: override,
      },
      visibleThinkingActivitiesDetails: {
        merge: override,
      },
      highImpactTeachingStrategiesDetails: {
        merge: override,
      },
    },
  },
  ActivitiesInfoBoxComponent: {
    keyFields: obj => generateKeyWithDefaultVersion(obj),
  },
  ActivitiesResponseComponent: {
    keyFields: obj => generateKeyWithDefaultVersion(obj),
  },
  ActivitiesVideoComponent: {
    keyFields: obj => generateKeyWithDefaultVersion(obj),
  },
  ActivitiesAudioComponent: {
    keyFields: obj => generateKeyWithDefaultVersion(obj),
  },
  ActivitiesImageSphereComponent: {
    keyFields: obj => generateKeyWithDefaultVersion(obj),
  },
  ActivitiesLinkComponent: {
    keyFields: obj => generateKeyWithDefaultVersion(obj),
  },
  ActivitiesWebToolComponent: {
    keyFields: obj => generateKeyWithDefaultVersion(obj),
  },
  ActivitiesDocumentComponent: {
    keyFields: obj => generateKeyWithDefaultVersion(obj),
  },
  ActivitiesEbookComponent: {
    keyFields: obj => generateKeyWithDefaultVersion(obj),
  },
  ActivitiesInteractiveEbookComponent: {
    keyFields: obj => generateKeyWithDefaultVersion(obj),
  },
  ActivitiesMultipleChoiceComponent: {
    keyFields: obj => generateKeyWithDefaultVersion(obj),
  },
  ActivitiesInteractiveResponseComponent: {
    keyFields: obj => generateKeyWithDefaultVersion(obj),
    fields: {
      properties: {
        merge: override,
      },
    },
  },
  ActivitiesRiveAnimationComponent: {
    keyFields: obj => generateKeyWithDefaultVersion(obj),
  },
  ActivitiesGifComponent: {
    keyFields: obj => generateKeyWithDefaultVersion(obj),
  },
  ActivitiesCategoriseResponseComponent: {
    keyFields: obj => generateKeyWithDefaultVersion(obj),
    fields: {
      properties: {
        merge: override,
      },
    },
  },
  ActivitiesTableComponent: {
    keyFields: obj => generateKeyWithDefaultVersion(obj),
  },
  ActivitiesDiscussionComponent: {
    keyFields: obj => generateKeyWithDefaultVersion(obj),
  },
  ActivitiesDrawComponent: {
    keyFields: obj => generateKeyWithDefaultVersion(obj),
  },
  ActivitiesFillInBlanksResponseComponent: {
    keyFields: obj => generateKeyWithDefaultVersion(obj),
  },
  ActivitiesInteractiveComponent: {
    keyFields: obj => generateKeyWithDefaultVersion(obj),
  },
  ActivitiesGuidedResearchComponent: {
    keyFields: obj => generateKeyWithDefaultVersion(obj),
  },
  ActivitiesSlideshowComponent: {
    keyFields: obj => generateKeyWithDefaultVersion(obj),
    fields: {
      slideshowImages: {
        merge: override,
      },
    },
  },
  ActivitiesColumnComponent: {
    keyFields: obj => generateColumnKeyWithDefaultVersion(obj),
    fields: {
      components: {
        merge: override,
      },
    },
  },
  ActivitiesColumnsComponent: {
    keyFields: obj => generateKeyWithDefaultVersion(obj),
    fields: {
      columns: {
        merge: override,
      },
    },
  },
  ActivitiesImageComponent: {
    keyFields: obj => generateKeyWithDefaultVersion(obj),
    fields: {
      stimulusResourceProperties: {
        merge: override,
      },
    },
  },
  ActivitiesSectionOverview: {
    fields: {
      subSectionsOverview: {
        merge: override,
      },
    },
  },
  ActivityAnswer: {
    keyFields: ['uuid'],
    fields: {
      answer: {
        merge: override,
      },
      answerReferences: {
        merge: override,
      },
    },
  },
  ActivitiesVocabularyComponent: {
    keyFields: obj => generateKeyWithDefaultVersion(obj),
    fields: {
      vocabularyDefinitions: {
        merge: override,
      },
    },
  },
};

export const interactiveLessonQueryTypePolicies: TypePolicies = {
  getLessonTeachingAndLearningSequence: {
    merge: override,
  },
  getAssessmentTeachingAndLearningSequence: {
    merge: override,
  },
  getMyAnswers: {
    merge: override,
  },
};
